import { msg, t } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchPatient,
  fetchPatients,
  createDeletedPatient,
  resolvePatient,
} from "./Patients";
import type { IListPatient } from "./Patients";
import type { IPatient } from "@medoma/models/patients";
import { logisticsApi } from "./ApiClient";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import { medicalCompetenceSchema } from "@medoma/models/shifts";
import { generateQueryString } from "./Helpers";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import {
  calendarDateSchema,
  dateSchema,
  humanFormat,
  machineFormat,
  timespanSchema,
} from "@medoma/models/date-and-time";
import {
  categorySchema,
  groupStatusSchema,
  type IActivityOccurrenceOrGroup,
  type IActivityOccurrenceOrGroupWithRequiredPatient,
  type IGroupStatus,
  type IMeasurementsType,
  isGroup,
  measurementSchema,
  videoTypeSchema,
} from "@medoma/models/activities";
import {
  type IUnenrichedActivityOccurrence,
  type IUnenrichedActivityOccurrenceOrGroup,
  type IUnenrichedGroupOfActivityOccurrence,
  unenrichedActivityOccurrencesAndGroupsSchema,
  unenrichedActivityOccurrencesAndGroupsWithRequiredPatientIdsSchema,
  unenrichedActivityOccurrenceSchema,
  unenrichedGroupOfActivityOccurrenceSchema,
} from "@medoma/models/activities.unenriched";
import { addressWithCoordinates } from "@medoma/models/addresses";
import { isBefore, startOfToday } from "date-fns";
import type { IConfirmDialogFunction } from "@medoma/components/Dialog/DialogContext";
import type { IUnplannedOccurrenceOrGroup } from "@medoma/models/activities";
import { unenrichedUnplannedOccurrencesAndGroupsSchema } from "@medoma/models/activities.unenriched";

export const activityCategoryDictionary = {
  HomeVisit: msg`Hembesök`,
  VideoCall: msg`Videosamtal`,
  ClinicVisit: msg`Mottagningsbesök`,
  PatientTask: msg`Patientaktivitet`,
  AdminTask: msg`Administrativ uppgift`,
  PatientMeasurementTask: msg`Egen mätning`,
  PhoneCall: msg`Telefonsamtal`,
};

const fetchPatientsForActivityOccurrencesAndGroups = (
  activityOccurrencesAndGroups: IUnenrichedActivityOccurrenceOrGroup[],
) =>
  fetchPatients({
    patientIds: deduplicatePrimitiveArray(
      activityOccurrencesAndGroups.flatMap(({ patientId }) =>
        patientId ? [patientId] : [],
      ),
    ),
  });

type GroupWithPatient<T extends IUnenrichedGroupOfActivityOccurrence> =
  T extends IUnenrichedGroupOfActivityOccurrence
    ? StrictOmit<T, "patientId" | "occurrences"> & {
        patient: IListPatient;
        occurrences: Array<
          StrictOmit<T["occurrences"][number], "patientId"> & {
            patient: IListPatient;
          }
        >;
      }
    : never;

const enrichGroupWithPatient = <
  T extends IUnenrichedGroupOfActivityOccurrence,
>({
  unenrichedGroup,
  patient,
}: {
  unenrichedGroup: T;
  patient: IListPatient;
}): GroupWithPatient<T> => {
  const {
    patientId: _droppedPatientId,
    occurrences,
    ...group
  } = unenrichedGroup;

  // The parsed group guarantees that its category matches its occurrence type.
  // Mapping the union loses that correlation, so restore it at this boundary.
  return {
    ...group,
    patient,
    occurrences: occurrences.map(
      ({ patientId: _droppedPatientId, ...occurrence }) => ({
        ...occurrence,
        patient,
      }),
    ),
  } as GroupWithPatient<T>;
};

const enrichActivityOccurrenceOrGroupWithRequiredPatient = ({
  activityOccurrenceOrGroup,
  patient,
}: {
  activityOccurrenceOrGroup: IUnenrichedActivityOccurrenceOrGroup;
  patient: IListPatient;
}): IActivityOccurrenceOrGroupWithRequiredPatient => {
  if (isGroup(activityOccurrenceOrGroup)) {
    return enrichGroupWithPatient({
      unenrichedGroup: activityOccurrenceOrGroup,
      patient,
    });
  }

  const { patientId: _droppedPatientId, ...activityOccurrence } =
    activityOccurrenceOrGroup;
  return { ...activityOccurrence, patient };
};

const parseAndEnrichActivityOccurrencesAndGroups = async (
  probableActivityOccurrencesAndGroups: unknown,
): Promise<IActivityOccurrenceOrGroup[]> => {
  const parsedActivityOccurrencesAndGroups =
    unenrichedActivityOccurrencesAndGroupsSchema.parse(
      probableActivityOccurrencesAndGroups,
    );

  if (parsedActivityOccurrencesAndGroups.length === 0) {
    return [];
  }

  const patients = await fetchPatientsForActivityOccurrencesAndGroups(
    parsedActivityOccurrencesAndGroups,
  );

  return parsedActivityOccurrencesAndGroups.map((activityOccurrenceOrGroup) => {
    if (activityOccurrenceOrGroup.category === categorySchema.enum.AdminTask) {
      const { patientId, ...adminTask } = activityOccurrenceOrGroup;
      if (!patientId) {
        return { ...adminTask, patient: null };
      }

      return enrichActivityOccurrenceOrGroupWithRequiredPatient({
        activityOccurrenceOrGroup,
        patient: resolvePatient({ patientId, patients }),
      });
    }

    return enrichActivityOccurrenceOrGroupWithRequiredPatient({
      activityOccurrenceOrGroup,
      patient: resolvePatient({
        patientId: activityOccurrenceOrGroup.patientId,
        patients,
      }),
    });
  });
};

const parseAndEnrichActivityOccurrencesAndGroupsWithRequiredPatient = async (
  probableActivityOccurrencesAndGroups: unknown,
): Promise<IActivityOccurrenceOrGroupWithRequiredPatient[]> => {
  const parsedActivityOccurrencesAndGroups =
    unenrichedActivityOccurrencesAndGroupsWithRequiredPatientIdsSchema.parse(
      probableActivityOccurrencesAndGroups,
    );

  if (parsedActivityOccurrencesAndGroups.length === 0) {
    return [];
  }

  const patients = await fetchPatientsForActivityOccurrencesAndGroups(
    parsedActivityOccurrencesAndGroups,
  );

  return parsedActivityOccurrencesAndGroups.map((activityOccurrenceOrGroup) =>
    enrichActivityOccurrenceOrGroupWithRequiredPatient({
      activityOccurrenceOrGroup,
      patient: resolvePatient({
        patientId: activityOccurrenceOrGroup.patientId,
        patients,
      }),
    }),
  );
};

export async function fetchActivityOccurrencesAndGroups(
  from?: string,
  to?: string,
) {
  const queryString = generateQueryString({ from, to });
  return await parseAndEnrichActivityOccurrencesAndGroups(
    await logisticsApi.get(`/occurrences${queryString}`).json(),
  );
}

export async function fetchUncancelledHomeVisitActivityOccurrences(
  from?: string,
  to?: string,
) {
  const queryString = generateQueryString({ from, to });
  const activityOccurrencesAndGroups =
    await parseAndEnrichActivityOccurrencesAndGroups(
      await logisticsApi.get(`/occurrences${queryString}`).json(),
    );

  return activityOccurrencesAndGroups.flatMap((occurrenceOrGroup) => {
    if (occurrenceOrGroup.category !== categorySchema.enum.HomeVisit) {
      return [];
    }

    if (!isGroup(occurrenceOrGroup)) {
      return occurrenceOrGroup;
    }

    // A cancelled group is not a home visit that will take place.
    return occurrenceOrGroup.visitStatus === groupStatusSchema.enum.cancelled
      ? []
      : occurrenceOrGroup.occurrences;
  });
}

// Only ever called with a single occurrence, never a group, so the narrower
// parameter keeps the occurrence-shaped result the callers rely on. The patient
// is a whole patient rather than the list projection the bulk path resolves,
// which satisfies every consumer that asks for the list shape.
const enrichActivityOccurrenceWithPatient = ({
  activityOccurrence,
  patient,
}: {
  activityOccurrence: IUnenrichedActivityOccurrence;
  patient: IPatient;
}) => {
  const { patientId: _droppedPatientId, ...occurrence } = activityOccurrence;
  return {
    ...occurrence,
    patient,
  };
};

async function fetchActivityOccurrence(
  activityId: string,
  occurrenceId: string,
) {
  const parsedActivityOccurrence = await logisticsApi
    .get(`/activities/${activityId}/occurrences/${occurrenceId}`)
    .json(unenrichedActivityOccurrenceSchema);

  const patientId = parsedActivityOccurrence.patientId;
  if (!patientId) {
    if (parsedActivityOccurrence.category !== categorySchema.enum.AdminTask) {
      // The schema makes patientId required for every other category, so the
      // parse above would have rejected this. Saying so here keeps the case out
      // of the return type instead of widening it for every caller.
      throw new Error(
        `Expected a patient id on a ${parsedActivityOccurrence.category} occurrence.`,
      );
    }

    // An admin task is allowed to have no patient. It still leaves here
    // enriched, with the absence expressed as the null patient every caller
    // already reads, rather than as a patientId nobody downstream resolves.
    const { patientId: _droppedPatientId, ...adminTask } =
      parsedActivityOccurrence;
    return { ...adminTask, patient: null };
  }

  try {
    const patient = await fetchPatient(patientId);
    return enrichActivityOccurrenceWithPatient({
      activityOccurrence: parsedActivityOccurrence,
      patient,
    });
  } catch (_error) {
    const patient = createDeletedPatient(patientId);
    return enrichActivityOccurrenceWithPatient({
      activityOccurrence: parsedActivityOccurrence,
      patient,
    });
  }
}

async function fetchGroup(id: string) {
  const [group] = await parseAndEnrichActivityOccurrencesAndGroups([
    await logisticsApi.get(`/occurrence-groups/${id}`).json(),
  ]);

  if (!group || !isGroup(group)) {
    throw new Error(`Expected a group of occurrences for group id ${id}.`);
  }

  return group;
}

export const activityKeys = {
  all: ["activities"] as const,
  lists: () => [...activityKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...activityKeys.lists(), filters] as const,
  detail: (id: string, filters?: Record<string, unknown>) =>
    [...activityKeys.all, id, "details", filters] as const,
};

export const activityOccurrenceAndGroupKeys = {
  all: () => [...activityKeys.all, "occurrencesAndGroups"] as const,
  lists: () => [...activityOccurrenceAndGroupKeys.all(), "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...activityOccurrenceAndGroupKeys.lists(), filters] as const,
  detail: (id: string) =>
    [...activityOccurrenceAndGroupKeys.all(), id, "details"] as const,
};

const ONE_MINUTE = 1000 * 60;

export const useActivityOccurrencesAndGroups = (
  fromDate: Date,
  toDate: Date,
) => {
  const from = machineFormat(fromDate, "yyyy-MM-dd");
  const to = machineFormat(toDate, "yyyy-MM-dd");
  return useQuery({
    queryKey: activityOccurrenceAndGroupKeys.list({ from, to }),
    queryFn: () => fetchActivityOccurrencesAndGroups(from, to),
    // Activity occurrences have time based status logic, so we need to refetch them often where status matters/is shown.
    refetchInterval: ONE_MINUTE,
    throwOnError: true,
  });
};

export const useActivityOccurrence = (
  activityId: string,
  occurrenceId: string,
) => {
  return useQuery({
    queryKey: activityOccurrenceAndGroupKeys.detail(
      `${activityId}${occurrenceId}`,
    ),
    queryFn: () => fetchActivityOccurrence(activityId, occurrenceId),
  });
};

export const groupQueryOptions = (id: string) =>
  queryOptions({
    queryKey: activityOccurrenceAndGroupKeys.detail(`${id}`),
    queryFn: () => fetchGroup(id),
  });

export async function fetchPatientsActivityOccurrencesAndGroups(
  patientId: string,
  from?: string,
  to?: string,
) {
  return await parseAndEnrichActivityOccurrencesAndGroupsWithRequiredPatient(
    await logisticsApi
      .get(
        `patients/${patientId}/occurrences${generateQueryString({
          from,
          to,
          status: [
            activityStatusSchema.enum.active,
            activityStatusSchema.enum.closed,
          ],
        })}`,
      )
      .json(),
  );
}

export const usePatientsActivityOccurrencesAndGroups = (
  patientId: string,
  fromDate?: Date,
  toDate?: Date,
) => {
  const from = fromDate ? machineFormat(fromDate, "yyyy-MM-dd") : undefined;
  const to = toDate ? machineFormat(toDate, "yyyy-MM-dd") : undefined;
  return useQuery({
    queryKey: activityOccurrenceAndGroupKeys.list({ patientId, from, to }),
    queryFn: () =>
      fetchPatientsActivityOccurrencesAndGroups(patientId, from, to),
    // Activity occurrences have time based status logic, so we need to refetch them often where status matters/is shown.
    refetchInterval: ONE_MINUTE,
    // Avoid flashing when fetching more history: https://tanstack.com/query/latest/docs/framework/react/guides/paginated-queries#better-paginated-queries-with-placeholderdata
    placeholderData: keepPreviousData,
  });
};

async function fetchActivity(activityId: string) {
  const parsedActivity = await logisticsApi
    .get(`/activities/${activityId}`)
    .json(activitySchema);
  return parsedActivity;
}

export const useActivity = (activityId: string) => {
  return useQuery({
    queryKey: activityKeys.detail(activityId),
    queryFn: () => {
      return fetchActivity(activityId);
    },
  });
};

export const closeActivity = async (activityId: string) => {
  await logisticsApi.delete(`/activities/${activityId}`);
};

export const removeActivityOccurrence = async ({
  activityId,
  occurrenceId,
}: {
  activityId: string;
  occurrenceId: string;
}) => {
  await logisticsApi.delete(
    `/activities/${activityId}/occurrences/${occurrenceId}`,
  );
};

export const allocateShiftsToActivityOccurrence = async (
  shiftIds: number[],
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `/activities/${activityId}/occurrences/${occurrenceId}/allocate`,
    {
      json: { shifts: shiftIds },
    },
  );
};

export const deallocateShiftsFromActivityOccurrence = async (
  shiftIds: number[],
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `/activities/${activityId}/occurrences/${occurrenceId}/deallocate`,
    {
      json: { shifts: shiftIds },
    },
  );
};

export const finishActivityOccurrence = async (
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `activities/${activityId}/occurrences/${occurrenceId}/finish`,
  );
};

export const finishActivityOccurrenceOnBehalf = async (
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `activities/${activityId}/occurrences/${occurrenceId}/finish-on-behalf`,
  );
};

export const resetActivityOccurrence = async (
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `activities/${activityId}/occurrences/${occurrenceId}/reset`,
  );
};

export const finishOccurrenceGroupOnBehalf = async ({
  id,
  occurrenceIds,
}: {
  id: string;
  occurrenceIds: string[];
}) => {
  await logisticsApi.post(`/occurrence-groups/${id}/finish-on-behalf`, {
    json: { occurrenceIds },
  });
};

export const removeOccurrenceGroup = async (occurrenceGroupId: string) => {
  await logisticsApi.post(`/occurrence-groups/${occurrenceGroupId}/remove`, {
    json: { occurrenceGroupId },
  });
};

export const cancelGroupReasonOptionSchema = z.enum([
  "NotHome",
  "Hospitalized",
  "Other",
]);
export type ICancelGroupReasonOption = z.infer<
  typeof cancelGroupReasonOptionSchema
>;

const cancelGroupReasonOptionDictionary = {
  [cancelGroupReasonOptionSchema.enum.NotHome]: msg`Ej hemma`,
  [cancelGroupReasonOptionSchema.enum.Hospitalized]: msg`Inlagd`,
} as const;

export const displayCancelGroupReason = (
  reason: string,
  translator: (descriptor: MessageDescriptor) => string,
): string => {
  const descriptor =
    cancelGroupReasonOptionDictionary[
      reason as keyof typeof cancelGroupReasonOptionDictionary
    ];
  return descriptor ? translator(descriptor) : reason;
};

export const isCancelledWithReason = <
  T extends {
    visitStatus?: IGroupStatus | null;
    visitCancellationReason?: string | null;
  },
>(
  groupOrVisit: T,
): groupOrVisit is T & {
  visitStatus: typeof groupStatusSchema.enum.cancelled;
  visitCancellationReason: string;
} =>
  groupOrVisit.visitStatus === groupStatusSchema.enum.cancelled &&
  groupOrVisit.visitCancellationReason !== null &&
  groupOrVisit.visitCancellationReason !== undefined;

export const cancelOccurrenceGroup = async ({
  occurrenceGroupId,
  reason,
}: {
  occurrenceGroupId: string;
  reason: string;
}) => {
  await logisticsApi.post(`/occurrence-groups/${occurrenceGroupId}/cancel`, {
    json: { reason },
  });
};

export const revertCancellationOccurrenceGroup = async (
  occurrenceGroupId: string,
) => {
  await logisticsApi.post(
    `/occurrence-groups/${occurrenceGroupId}/revert-cancellation`,
  );
};

export const revertOccurrenceGroupToPlanned = async (
  occurrenceGroupId: string,
) => {
  await logisticsApi.post(
    `/occurrence-groups/${occurrenceGroupId}/revert-to-planned`,
  );
};

export const ANY_TIME_OF_DAY = "AnyTimeOfDay" as const;
const anyTimeOfDaySchema = z.literal(ANY_TIME_OF_DAY);

// ------------------------------------------------------------
// TODO: Replace these with zod time() when zocker supports it (most likely in version 2, have not checked yet though)
// ------------------------------------------------------------
// Matches time in HH:MM format (24-hour clock)
// - Hours (HH): 00-23 (requires leading zero)
// - Minutes (MM): 00-59 (requires leading zero)
// Examples: "09:30", "14:00", "23:59"
const timeRegex = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
// Matches time span in HH:MM:SS format (24-hour clock)
// - Hours (HH): 0-23 (leading zero optional)
// - Minutes (MM): 00-59 (requires leading zero)
// - Seconds (SS): 00-59 (requires leading zero)
// - Optional milliseconds (.9999999): 0-9 digits after decimal point
// Examples: "9:30:00", "14:00:00", "23:59:59", "23:59:59.9999999"
const dateRegex =
  /^(?:(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01]))$/;

const timeSchema = z.union([z.string().regex(timeRegex), anyTimeOfDaySchema]);

// As per recurrencesPerDayOptions in timeSlotsUtils
const MAX_OCCURRENCES_PER_DAY = 7;
const timesSchema = z.union([
  z.array(z.string().regex(timeRegex)).max(MAX_OCCURRENCES_PER_DAY),
  anyTimeOfDaySchema,
]);
export type ITime = z.infer<typeof timeSchema>;

export const weekdaySchema = z.enum([
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]);
export type IWeekday = z.infer<typeof weekdaySchema>;

const intervalSchema = z.number().int().positive();

const frequencyBaseSchema = z.object({
  start: dateSchema,
  end: dateSchema.nullish(),
  span: timespanSchema,
  times: timesSchema,
});

const weekdayFrequencySchema = frequencyBaseSchema.extend({
  days: z.array(weekdaySchema),
});

const intervalFrequencySchema = frequencyBaseSchema.extend({
  interval: intervalSchema,
});

export const frequencySchema = z.union([
  weekdayFrequencySchema,
  intervalFrequencySchema,
]);
export type IRecurringActivitySchedule = z.infer<typeof frequencySchema>;

export const oneTimeSchema = z.object({
  date: z.string().regex(dateRegex),
  time: z.union([z.string().regex(timeRegex), anyTimeOfDaySchema]),
  span: timespanSchema,
});
export type IOneTimeActivitySchedule = z.infer<typeof oneTimeSchema>;

export const activityScheduleSchema = z.union([oneTimeSchema, frequencySchema]);
export type IActivitySchedule = z.infer<typeof activityScheduleSchema>;

const _rescheduleActivitySchema = z.union([
  weekdayFrequencySchema.omit({ start: true, end: true }),
  intervalFrequencySchema.omit({ start: true, end: true }),
]);
export type IRescheduleActivity = z.infer<typeof _rescheduleActivitySchema>;

export const activityStatusSchema = z.enum(["active", "closed"]);

const activityBaseSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullish(),
  schedule: activityScheduleSchema,
  recurring: z.boolean(),
  category: categorySchema,
  duration: z.number().int(),
  status: activityStatusSchema,
  hidden: z.boolean(),
});

const newActivityOmit = {
  id: true,
  recurring: true,
  schedule: true,
  status: true,
} as const;

const newActivityExtend = {
  activityId: z.uuid(),
  timespan: z.string(),
  startDate: calendarDateSchema,
  templateId: z.uuid().optional(),
  templateRevision: z.number().int().optional(),
};

const customRecurrenceExtend = {
  days: z.array(weekdaySchema),
  times: timesSchema,
  endDate: calendarDateSchema.optional(),
};

const preconfiguredRecurrenceExtend = {
  interval: intervalSchema,
  times: timesSchema,
  endDate: calendarDateSchema.optional(),
};

const oneTimeExtend = {
  time: timeSchema,
};

// HOME VISIT
export const activityHomeVisitSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.HomeVisit),
  doubleStaffing: z.boolean(),
  requiredCompetences: z.array(medicalCompetenceSchema),
  patientId: z.uuid(),
});

const newHomeVisitActivityBaseSchema = activityHomeVisitSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeHomeVisitActivitySchema =
  newHomeVisitActivityBaseSchema.extend(oneTimeExtend);

const newRecurringHomeVisitActivitySchema = z.union([
  newHomeVisitActivityBaseSchema.extend(customRecurrenceExtend),
  newHomeVisitActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newHomeVisitActivitySchema = z.union([
  newOneTimeHomeVisitActivitySchema,
  newRecurringHomeVisitActivitySchema,
]);
export type INewHomeVisitActivity = z.infer<typeof newHomeVisitActivitySchema>;

const _activityRegisterRetroactivelySchema = z.object({
  activityId: z.uuid(),
  careEpisodeId: z.uuid(),
  title: z.string(),
  date: calendarDateSchema,
  time: z.string(),
  category: categorySchema,
  duration: z.number().int().positive(),
  shiftId: z.string(),
  description: z.string().nullish(),
});

export type IActivityRegisteredRetroactively = z.infer<
  typeof _activityRegisterRetroactivelySchema
>;

const retroactiveActivityInGroupSchema = z.object({
  activityId: z.uuid(),
  title: z.string(),
  description: z.string().nullish(),
  duration: z.number().int().positive(),
});

const _activityGroupRegisterRetroactivelySchema = z.object({
  careEpisodeId: z.uuid(),
  shiftId: z.string(),
  date: calendarDateSchema,
  time: z.string(),
  category: categorySchema,
  activities: z.array(retroactiveActivityInGroupSchema).min(1),
});

export type IActivityGroupRegisteredRetroactively = z.infer<
  typeof _activityGroupRegisterRetroactivelySchema
>;

// VIDEO
export const activityVideoSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.VideoCall),
  requiredCompetences: z.array(medicalCompetenceSchema),
  type: videoTypeSchema,
  patientId: z.uuid(),
});
const newVideoActivityBaseSchema = activityVideoSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeVideoActivitySchema =
  newVideoActivityBaseSchema.extend(oneTimeExtend);

const newRecurringVideoActivitySchema = z.union([
  newVideoActivityBaseSchema.extend(customRecurrenceExtend),
  newVideoActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newVideoActivitySchema = z.union([
  newOneTimeVideoActivitySchema,
  newRecurringVideoActivitySchema,
]);
export type INewVideoActivity = z.infer<typeof newVideoActivitySchema>;

// CLINIC VISIT
export const activityClinicVisitSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.ClinicVisit),
  requiredCompetences: z.array(medicalCompetenceSchema),
  patientId: z.uuid(),
  address: addressWithCoordinates.nullish(),
});
const newClinicVisitActivityBaseSchema = activityClinicVisitSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeClinicVisitActivitySchema =
  newClinicVisitActivityBaseSchema.extend(oneTimeExtend);

const newRecurringClinicVisitActivitySchema = z.union([
  newClinicVisitActivityBaseSchema.extend(customRecurrenceExtend),
  newClinicVisitActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newClinicVisitActivitySchema = z.union([
  newOneTimeClinicVisitActivitySchema,
  newRecurringClinicVisitActivitySchema,
]);
export type INewClinicVisitActivity = z.infer<
  typeof newClinicVisitActivitySchema
>;

// PATIENT
export const activityPatientTaskSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.PatientTask),
  patientId: z.uuid(),
});

const newPatientTaskActivityBaseSchema = activityPatientTaskSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimePatientTaskActivitySchema =
  newPatientTaskActivityBaseSchema.extend(oneTimeExtend);

const newRecurringPatientTaskActivitySchema = z.union([
  newPatientTaskActivityBaseSchema.extend(customRecurrenceExtend),
  newPatientTaskActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newPatientTaskActivitySchema = z.union([
  newOneTimePatientTaskActivitySchema,
  newRecurringPatientTaskActivitySchema,
]);
export type INewPatientTaskActivity = z.infer<
  typeof newPatientTaskActivitySchema
>;

// ADMIN
export const activityAdminTaskSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.AdminTask),
  patientId: z.uuid().nullish(),
  requiredCompetences: z.array(medicalCompetenceSchema),
});

const newAdminTaskActivityBaseSchema = activityAdminTaskSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeAdminTaskActivitySchema =
  newAdminTaskActivityBaseSchema.extend(oneTimeExtend);

const newRecurringAdminTaskActivitySchema = z.union([
  newAdminTaskActivityBaseSchema.extend(customRecurrenceExtend),
  newAdminTaskActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newAdminTaskActivitySchema = z.union([
  newOneTimeAdminTaskActivitySchema,
  newRecurringAdminTaskActivitySchema,
]);
export type INewAdminTaskActivity = z.infer<typeof newAdminTaskActivitySchema>;

// PHONE CALL
const activityPhoneCallSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.PhoneCall),
  requiredCompetences: z.array(medicalCompetenceSchema),
  patientId: z.uuid(),
});
const newPhoneCallActivityBaseSchema = activityPhoneCallSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimePhoneCallActivitySchema =
  newPhoneCallActivityBaseSchema.extend(oneTimeExtend);

const newRecurringPhoneCallActivitySchema = z.union([
  newPhoneCallActivityBaseSchema.extend(customRecurrenceExtend),
  newPhoneCallActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newPhoneCallActivitySchema = z.union([
  newOneTimePhoneCallActivitySchema,
  newRecurringPhoneCallActivitySchema,
]);
export type INewPhoneCallActivity = z.infer<typeof newPhoneCallActivitySchema>;

//  PATIENT MEASUREMENTS
export const activityPatientMeasurementTaskSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.PatientMeasurementTask),
  patientId: z.uuid(),
  measurements: z.array(measurementSchema),
});

const newPatientMeasurementTaskActivityBaseSchema =
  activityPatientMeasurementTaskSchema
    .omit(newActivityOmit)
    .extend(newActivityExtend);

const newOneTimePatientMeasurementTaskActivitySchema =
  newPatientMeasurementTaskActivityBaseSchema.extend(oneTimeExtend);

const newRecurringPatientMeasurementTaskActivitySchema = z.union([
  newPatientMeasurementTaskActivityBaseSchema.extend(customRecurrenceExtend),
  newPatientMeasurementTaskActivityBaseSchema.extend(
    preconfiguredRecurrenceExtend,
  ),
]);

const newPatientMeasurementTaskActivitySchema = z.union([
  newOneTimePatientMeasurementTaskActivitySchema,
  newRecurringPatientMeasurementTaskActivitySchema,
]);
export type INewPatientMeasurementTaskActivity = z.infer<
  typeof newPatientMeasurementTaskActivitySchema
>;

const _newActivitySchema = z.union([
  newHomeVisitActivitySchema,
  newVideoActivitySchema,
  newClinicVisitActivitySchema,
  newPatientTaskActivitySchema,
  newAdminTaskActivitySchema,
  newPatientMeasurementTaskActivitySchema,
  newPhoneCallActivitySchema,
]);
export type INewActivity = z.infer<typeof _newActivitySchema>;

const encodeNewActivityDates = (newActivity: INewActivity) => ({
  ...newActivity,
  startDate: calendarDateSchema.encode(newActivity.startDate),
  endDate:
    "endDate" in newActivity
      ? calendarDateSchema.optional().encode(newActivity.endDate)
      : undefined,
});

export async function addActivity(newActivity: INewActivity) {
  await logisticsApi.post("/activities", {
    json: encodeNewActivityDates(newActivity),
  });
}

export async function registerActivityRetroactively(
  activityRegisteredRetroactively: IActivityRegisteredRetroactively,
) {
  await logisticsApi.post("/activities/retroactive", {
    json: {
      ...activityRegisteredRetroactively,
      date: calendarDateSchema.encode(activityRegisteredRetroactively.date),
    },
  });
}

export async function registerActivitiesRetroactivelyBulk(
  activityGroup: IActivityGroupRegisteredRetroactively,
) {
  await logisticsApi.post("/occurrence-groups/retroactive", {
    json: {
      ...activityGroup,
      date: calendarDateSchema.encode(activityGroup.date),
    },
  });
}

export async function addActivitiesFromGroupTemplate({
  templateId,
  templateRevision,
  newActivities,
}: {
  templateId: string;
  templateRevision: number;
  newActivities: INewActivity[];
}) {
  await logisticsApi.post("/activities/bulk", {
    json: {
      templateId,
      templateRevision,
      activities: newActivities.map(encodeNewActivityDates),
    },
  });
}

export const activitySchema = z.union([
  activityHomeVisitSchema,
  activityVideoSchema,
  activityClinicVisitSchema,
  activityPatientTaskSchema,
  activityAdminTaskSchema,
  activityPatientMeasurementTaskSchema,
  activityPhoneCallSchema,
]);

export type IActivity = z.infer<typeof activitySchema>;

const _newActivityInGroupSchema = newHomeVisitActivityBaseSchema
  .pick({
    activityId: true,
    title: true,
    description: true,
    requiredCompetences: true,
    duration: true,
  })
  .extend({
    groupId: z.string(),
  });

export type INewActivityInGroup = z.infer<typeof _newActivityInGroupSchema>;

export const addActivityInGroup = async (
  newActivityInGroup: INewActivityInGroup,
) => {
  await logisticsApi.post("/activities/contextual", {
    json: newActivityInGroup,
  });
};

export const updateTitle = async (activityId: string, title: string) => {
  const patch = {
    op: title ? "replace" : "remove",
    path: "/title",
    value: title,
  };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const updateDescription = async (
  activityId: string,
  description: string,
) => {
  const patch = {
    op: description ? "replace" : "remove",
    path: "/description",
    value: description,
  };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const updateHidden = async (activityId: string, hidden: boolean) => {
  const patch = {
    op: "replace",
    path: "/hidden",
    value: hidden,
  };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const updateRequiredCompetences = async (
  activityId: string,
  requiredCompetences: IMedicalCompetence[],
) => {
  const patch =
    requiredCompetences.length > 0
      ? { op: "replace", path: "/competence", value: requiredCompetences }
      : { op: "remove", path: "/competence" };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const updateMeasurements = async (
  activityId: string,
  measurements: IMeasurementsType[],
) => {
  const patch = {
    op: "replace",
    path: "/measurements",
    value: measurements,
  };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const updateDoubleStaffing = async (
  activityId: string,
  doubleStaffing: boolean,
) => {
  const patch = {
    op: "replace",
    path: "/doubleStaffing",
    value: doubleStaffing,
  };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const updateDuration = async (activityId: string, duration: number) => {
  const patch = {
    op: "replace",
    path: "/duration",
    value: duration,
  };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const updateEndDate = async (activityId: string, endDateTime: Date) => {
  const patch = {
    op: "replace",
    path: "/endDate",
    value: endDateTime,
  };
  await logisticsApi.patch(`/activities/${activityId}`, { json: [patch] });
};

export const rescheduleActivity = async (
  activityId: string,
  data: IRescheduleActivity,
  confirm: IConfirmDialogFunction,
) => {
  const rescheduleUrl = `/activities/${activityId}/reschedule`;

  const validationResult = await logisticsApi
    .post(`${rescheduleUrl}/validate`, { json: data })
    .json(z.array(unenrichedActivityOccurrenceSchema));

  const humanReadableListOfOccurrencesNotPassingValidation = validationResult
    .map(
      ({ start, note }) =>
        `- ${humanFormat(start, {
          weekday: "long",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        })}${note ? ` (${note})` : ""}`,
    )
    .join("\n");
  const TWO_LINE_BREAKS = "\n\n";
  if (
    validationResult.length === 0 ||
    (await confirm({
      title: t`Ändra schema`,
      message: t`Följande tillfällen har planerats in i besök, fått en kommentar, eller ändrad tid:${TWO_LINE_BREAKS}${humanReadableListOfOccurrencesNotPassingValidation}${TWO_LINE_BREAKS}När du fortsätter så ersätts dessa av nya tillfällen i enlighet med det nya schemat. Besöksplaneringar, kommentarer och tidsändringar för individuella tillfällen kommer därmed att behöva göras om.`,
    }))
  ) {
    await logisticsApi.post(rescheduleUrl, { json: data });
    return true;
  }
  return false;
};

export type IRescheduledActivityOccurrence = {
  date: Date;
  time: ITime;
  span?: string;
};
export const rescheduleActivityOccurrence = async (
  activityId: string,
  occurrenceId: string,
  data: IRescheduledActivityOccurrence,
) => {
  await logisticsApi.post(
    `/activities/${activityId}/occurrences/${occurrenceId}/reschedule`,
    { json: { ...data, date: calendarDateSchema.encode(data.date) } },
  );
};

export type IRescheduleGroup = {
  date: Date;
  time: ITime;
  span?: string;
};
export const rescheduleGroup = async (
  groupId: string,
  data: IRescheduleGroup,
) => {
  await logisticsApi.post(`/occurrence-groups/${groupId}/reschedule`, {
    json: { ...data, date: calendarDateSchema.encode(data.date) },
  });
};

export const changeActivityOccurrenceTimespan = async (
  activityId: string,
  occurrenceId: string,
  timespan: string,
) => {
  await logisticsApi.post(
    `/activities/${activityId}/occurrences/${occurrenceId}/change-timespan`,
    { json: { timespan } },
  );
};

const fetchEligibleGroups = async ({
  activityId,
  occurrenceId,
}: {
  activityId: string;
  occurrenceId: string;
}) => {
  const eligibleGroups = await logisticsApi
    .get(
      `/activities/${activityId}/occurrences/${occurrenceId}/eligible-occurrence-groups`,
    )
    .json(z.array(unenrichedGroupOfActivityOccurrenceSchema));

  if (eligibleGroups.length === 0) {
    return [];
  }

  const patients =
    await fetchPatientsForActivityOccurrencesAndGroups(eligibleGroups);

  return eligibleGroups.map((group) =>
    enrichGroupWithPatient({
      unenrichedGroup: group,
      patient: resolvePatient({ patientId: group.patientId, patients }),
    }),
  );
};

const eligibleGroupsKeys = {
  all: ["eligibleGroups"] as const,
  detail: ({
    activityId,
    occurrenceId,
  }: {
    activityId: string;
    occurrenceId: string;
  }) => [...eligibleGroupsKeys.all, activityId, occurrenceId] as const,
};
export const useEligibleGroups = ({
  activityId,
  occurrenceId,
}: {
  activityId: string;
  occurrenceId: string;
}) => {
  return useQuery({
    queryKey: eligibleGroupsKeys.detail({ activityId, occurrenceId }),
    queryFn: () => fetchEligibleGroups({ activityId, occurrenceId }),
  });
};

export const changeGroup = async (
  activityId: string,
  occurrenceId: string,
  groupId: string,
) => {
  await logisticsApi.post(
    `/activities/${activityId}/occurrences/${occurrenceId}/move`,
    {
      json: { targetGroupId: groupId },
    },
  );
};

const parseAndEnrichUnplannedOccurrencesAndGroups = async (
  probableUnplannedOccurrencesAndGroups: unknown,
): Promise<IUnplannedOccurrenceOrGroup[]> => {
  const unplannedOccurrencesAndGroups =
    unenrichedUnplannedOccurrencesAndGroupsSchema.parse(
      probableUnplannedOccurrencesAndGroups,
    );

  if (unplannedOccurrencesAndGroups.length === 0) {
    return [];
  }

  const patients = await fetchPatientsForActivityOccurrencesAndGroups(
    unplannedOccurrencesAndGroups,
  );

  return unplannedOccurrencesAndGroups.map((activityOccurrenceOrGroup) => {
    if (activityOccurrenceOrGroup.category === categorySchema.enum.AdminTask) {
      const { patientId, ...adminTask } = activityOccurrenceOrGroup;
      return {
        ...adminTask,
        patient: patientId ? resolvePatient({ patientId, patients }) : null,
      };
    }

    if (isGroup(activityOccurrenceOrGroup)) {
      return enrichGroupWithPatient({
        unenrichedGroup: activityOccurrenceOrGroup,
        patient: resolvePatient({
          patientId: activityOccurrenceOrGroup.patientId,
          patients,
        }),
      });
    }

    const { patientId, ...activityOccurrence } = activityOccurrenceOrGroup;
    return {
      ...activityOccurrence,
      patient: resolvePatient({ patientId, patients }),
    };
  });
};

export const useUnplannedOccurrencesAndGroups = ({
  fromDate,
  toDate,
}: {
  fromDate: Date;
  toDate: Date;
}) => {
  const from = machineFormat(fromDate, "yyyy-MM-dd");
  const to = machineFormat(toDate, "yyyy-MM-dd");
  return useQuery({
    queryKey: activityOccurrenceAndGroupKeys.list({
      from,
      to,
      unplanned: true,
    }),
    queryFn: () => fetchUnplannedOccurrencesAndGroups({ from, to }),
    // Activity occurrences have time based status logic, so we need to refetch them often where status matters/is shown.
    refetchInterval: ONE_MINUTE,
    throwOnError: true,
  });
};

async function fetchUnplannedOccurrencesAndGroups({
  from,
  to,
}: {
  from: string;
  to: string;
}) {
  const queryString = generateQueryString({ from, to });
  return parseAndEnrichUnplannedOccurrencesAndGroups(
    await logisticsApi.get(`/occurrences/unplanned${queryString}`).json(),
  );
}

export const setActivityOccurrenceNote = async ({
  activityId,
  occurrenceId,
  note,
}: {
  activityId: string;
  occurrenceId: string;
  note: string;
}) => {
  await logisticsApi.post(
    `/activities/${activityId}/occurrences/${occurrenceId}/set-note`,
    { json: { note } },
  );
};

export const closeReason = z.enum(["pause", "discharge-or-decline"]);

type ICloseReason = z.infer<typeof closeReason>;

type IActive = { closeReason?: never };
type IPause = {
  closeReason: "pause";
};
type IDischargeOrDecline = {
  closeReason: "discharge-or-decline";
  oldCareEpisodeId: string;
};
type IFetchPatientReason = {
  patientId: string;
} & (IActive | IPause | IDischargeOrDecline);
async function fetchPatientActivities(params: IFetchPatientReason) {
  const { patientId, ...otherParams } = params;
  const queryString = generateQueryString(otherParams);
  const parsedActivities = await logisticsApi
    .get(`patients/${params.patientId}/activities${queryString}`)
    .json(z.array(activitySchema));

  // Convert one-time activity times from UTC to local
  return parsedActivities.map(convertOneTimeActivityTimesToLocal);
}

export const patientActivitiesQueryOptions = (params: IFetchPatientReason) => {
  return queryOptions({
    queryKey: activityKeys.list(params),
    queryFn: () => fetchPatientActivities(params),
  });
};

export async function reactivateClosedActivities({
  patientId,
  closeReason,
  oldCareEpisodeId,
  activityIds,
}: {
  patientId: string;
  closeReason: ICloseReason;
  oldCareEpisodeId?: string | null;
  activityIds: string[];
}) {
  const queryString = generateQueryString({ closeReason, oldCareEpisodeId });
  await logisticsApi.post(`patients/${patientId}/activities${queryString}`, {
    json: { activityIds },
  });
}

/**
 * Converts UTC time to local time for one-time activities
 * @param time - UTC time in HH:MM format
 * @param date - Date string in YYYY-MM-DD format
 * @returns Local time in HH:MM format
 *
 * Example: If time is "10:30" UTC and local timezone is UTC+2 (Stockholm),
 * this will return "12:30" (local time)
 */
const convertUtcTimeToLocal = (time: string, date: string): string => {
  // Parse the UTC date and time
  const [year, month, day] = date.split("-").map(Number) as [
    number,
    number,
    number,
  ];
  const [hours, minutes] = time.split(":").map(Number) as [number, number];

  // Create a Date object representing the UTC time
  // Date.UTC creates a timestamp in milliseconds since epoch for the given UTC time
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));

  // When we call getHours() and getMinutes() on a Date object, JavaScript automatically
  // converts the UTC time to the browser's local timezone
  const localHours = utcDate.getHours();
  const localMinutes = utcDate.getMinutes();

  // Format back to HH:MM
  return `${String(localHours).padStart(2, "0")}:${String(localMinutes).padStart(2, "0")}`;
};

/**
 * Converts one-time activity times from UTC to local time
 */
const convertOneTimeActivityTimesToLocal = (activity: IActivity): IActivity => {
  // Only convert one-time activities
  if (activity.recurring || !("time" in activity.schedule)) {
    return activity;
  }

  // Skip if time is "AnyTimeOfDay"
  if (activity.schedule.time === ANY_TIME_OF_DAY) {
    return activity;
  }

  // Convert the time from UTC to local
  const localTime = convertUtcTimeToLocal(
    activity.schedule.time,
    activity.schedule.date,
  );

  return {
    ...activity,
    schedule: {
      ...activity.schedule,
      time: localTime,
    },
  };
};

const getPatientActiveActivitiesExceptForOneTimeActivitiesBeforeToday = async (
  patientId: string,
) => {
  const activities = await fetchPatientActivities({ patientId });
  const startToday = startOfToday();

  const activeActivitiesExceptForOneTimeActivitiesBeforeToday =
    activities.filter((activity) => {
      const isOneTimeActivityBeforeToday =
        activity.recurring === false &&
        "date" in activity.schedule &&
        isBefore(new Date(activity.schedule.date), startToday);

      return !isOneTimeActivityBeforeToday;
    });

  return activeActivitiesExceptForOneTimeActivitiesBeforeToday;
};

export const patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions =
  ({ patientId }: { patientId: string }) => {
    return queryOptions({
      queryKey: activityKeys.list({ patientId }),
      queryFn: () =>
        getPatientActiveActivitiesExceptForOneTimeActivitiesBeforeToday(
          patientId,
        ),
    });
  };
